import React, { useEffect, useState } from 'react';
import { MessageSquare, RefreshCw, ChevronDown, ChevronUp, Mail, Copy } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WINDOWS = [30, 60, 90, 180];

const REASON_COLORS = {
  missing_feature:           'bg-purple-500',
  hard_to_navigate:          'bg-amber-500',
  not_enough_value:          'bg-rose-500',
  privacy_security_concerns: 'bg-red-600',
  found_alternative:         'bg-cyan-500',
  other:                     'bg-slate-500',
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

const CancellationReasonsWidget = () => {
  const [windowDays, setWindowDays] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [followUpOpen, setFollowUpOpen] = useState(false);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const t = localStorage.getItem('auth_token') || localStorage.getItem('adminToken') || localStorage.getItem('token');
      const r = await fetch(`${BACKEND_URL}/api/admin/cancellation-reasons?days=${windowDays}`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [windowDays]);

  const copyEmails = () => {
    if (!data?.follow_up?.length) return;
    const emails = data.follow_up.map((u) => u.email).filter(Boolean).join(', ');
    navigator.clipboard.writeText(emails)
      .then(() => toast.success(`${data.follow_up.length} email${data.follow_up.length !== 1 ? 's' : ''} copied to clipboard`))
      .catch(() => toast.error('Could not copy — your browser blocked clipboard access'));
  };

  const total = data?.total || 0;
  const maxCount = Math.max(1, ...(data?.by_reason?.map((r) => r.count) || [1]));

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden" data-testid="cancellation-reasons-widget">
      <div className="p-3 sm:p-6 border-b border-gray-700">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-purple-900/40 rounded-lg flex items-center justify-center shrink-0">
              <MessageSquare className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-white">Cancellation Reasons</h3>
              <p className="text-sm text-gray-400 mt-1">Why subscribers cancelled in the last {windowDays} days.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={windowDays}
              onChange={(e) => setWindowDays(Number(e.target.value))}
              className="bg-gray-700 text-white rounded-lg px-3 py-2 text-sm border border-gray-600"
              data-testid="cancellation-window-select"
            >
              {WINDOWS.map((d) => <option key={d} value={d}>{d} days</option>)}
            </select>
            <button
              onClick={load}
              disabled={loading}
              className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded-lg transition flex items-center gap-2"
              data-testid="cancellation-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      <div className="p-3 sm:p-6">
        {error && <div className="text-sm text-red-300" data-testid="cancellation-error">{error}</div>}
        {!error && loading && !data && (
          <div className="text-center py-8 text-gray-400"><RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />Loading…</div>
        )}
        {!error && data && total === 0 && (
          <div className="text-center py-8 text-gray-400" data-testid="cancellation-empty">
            <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p>No cancellations in the last {windowDays} days.</p>
          </div>
        )}
        {!error && data && total > 0 && (
          <>
            <div className="flex items-baseline gap-2 mb-4">
              <span className="text-3xl font-bold text-white" data-testid="cancellation-total">{total}</span>
              <span className="text-sm text-gray-400">cancellation{total !== 1 ? 's' : ''} · {data.follow_up_count} opted in to follow-up</span>
            </div>

            {/* Horizontal bars per reason */}
            <div className="space-y-3" data-testid="cancellation-bars">
              {data.by_reason.map((row) => {
                const widthPct = total ? (row.count / maxCount) * 100 : 0;
                return (
                  <div key={row.reason} data-testid={`cancellation-row-${row.reason}`}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-gray-300">{row.label}</span>
                      <span className="text-white font-semibold">
                        {row.count} <span className="text-gray-500 font-normal">({row.pct}%)</span>
                      </span>
                    </div>
                    <div className="h-2 bg-gray-900 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${REASON_COLORS[row.reason] || 'bg-slate-500'} transition-all duration-500`}
                        style={{ width: `${widthPct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Follow-up section */}
            {data.follow_up_count > 0 && (
              <div className="mt-6 pt-4 border-t border-gray-700">
                <button
                  onClick={() => setFollowUpOpen(!followUpOpen)}
                  className="w-full flex items-center justify-between text-sm text-white hover:text-purple-300 transition"
                  data-testid="cancellation-followup-toggle"
                >
                  <span className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Follow-up list ({data.follow_up_count} user{data.follow_up_count !== 1 ? 's' : ''} opted in)
                  </span>
                  {followUpOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {followUpOpen && (
                  <div className="mt-3 space-y-3" data-testid="cancellation-followup-list">
                    <button
                      onClick={copyEmails}
                      className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded text-xs flex items-center gap-1.5"
                      data-testid="cancellation-copy-emails-btn"
                    >
                      <Copy className="w-3 h-3" /> Copy all emails
                    </button>
                    <div className="border border-gray-700 rounded-lg divide-y divide-gray-700 max-h-96 overflow-y-auto">
                      {data.follow_up.map((u, i) => (
                        <div key={u.user_id || i} className="p-3 hover:bg-gray-750" data-testid={`cancellation-followup-row-${i}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="text-sm text-white font-medium">{u.name || 'Unknown'}</div>
                              <div className="text-xs text-gray-400 truncate">{u.email}</div>
                              <div className="mt-1.5 text-xs text-gray-300">
                                <span className="text-purple-300">{u.reason_label}</span>
                                {u.reason_detail && <span className="text-gray-400"> — {u.reason_detail}</span>}
                              </div>
                            </div>
                            <div className="text-xs text-gray-500 text-right shrink-0">
                              <div>{fmtDate(u.cancelled_at)}</div>
                              <div className="opacity-60">{u.country || ''}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CancellationReasonsWidget;
